import React, { useState } from 'react';
import type { NavNode } from '../../lib/types';

interface NavTreeProps {
  nodes: NavNode[];
  currentPath: string | null;
  onSelect: (path: string) => void;
  draftPaths?: Set<string>;
}

interface NavItemProps {
  node: NavNode;
  currentPath: string | null;
  onSelect: (path: string) => void;
  draftPaths?: Set<string>;
  depth: number;
}

const NavItem: React.FC<NavItemProps> = ({
  node,
  currentPath,
  onSelect,
  draftPaths,
  depth,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const [expanded, setExpanded] = useState(true);
  const isActive = node.path === currentPath;
  const hasDraft = node.path && draftPaths?.has(node.path);

  const paddingLeft = `${(depth + 1) * 12}px`;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-1 px-2 py-1.5 text-left hover:bg-sidebar-hover text-sidebar-text text-sm"
          style={{ paddingLeft }}
        >
          <span className="text-sidebar-muted text-xs w-3 flex-shrink-0">
            {expanded ? '▼' : '▶'}
          </span>
          <span className="font-medium truncate">{node.title}</span>
        </button>
        {expanded && (
          <div>
            {node.children!.map((child, idx) => (
              <NavItem
                key={`${child.title}-${idx}`}
                node={child}
                currentPath={currentPath}
                onSelect={onSelect}
                draftPaths={draftPaths}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => node.path && onSelect(node.path)}
      disabled={!node.path}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm transition-colors ${
        isActive
          ? 'bg-sidebar-active text-white'
          : 'text-sidebar-text hover:bg-sidebar-hover'
      } ${!node.path ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
      style={{ paddingLeft }}
    >
      <span className="text-sidebar-muted text-xs w-3 flex-shrink-0">📄</span>
      <span className="truncate flex-1">{node.title}</span>
      {hasDraft && (
        <span
          className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"
          title="Has unsaved draft"
        />
      )}
    </button>
  );
};

export const NavTree: React.FC<NavTreeProps> = ({
  nodes,
  currentPath,
  onSelect,
  draftPaths,
}) => {
  if (nodes.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sidebar-muted text-sm">
        No pages found. Check mkdocs.yml navigation config.
      </div>
    );
  }

  return (
    <div className="py-2">
      {nodes.map((node, idx) => (
        <NavItem
          key={`${node.title}-${idx}`}
          node={node}
          currentPath={currentPath}
          onSelect={onSelect}
          draftPaths={draftPaths}
          depth={0}
        />
      ))}
    </div>
  );
};
